/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import {actionMatcher, getValue, InitState, NgxsPlugin, setValue, UpdateState} from '@ngxs/store';
import {Inject, Injectable, InjectionToken} from '@angular/core';
import {LoggerService} from '../../modules/shared/services/logger.service';
import {NgxsSnapshotPluginGroupOptions, NgxsSnapshotPluginOptions} from './ngxs-snapshot.model';
import {ClearSnapshots, RestoreSnapshot} from './ngxs-snapshot.actions';
import {DashGisImmutable} from '../../modules/shared/dash-gis-immutable';
import {isEmpty, isEqual} from 'lodash-es';

export const NGXS_SNAPSHOT_PLUGIN_OPTIONS = new InjectionToken('NGXS_SNAPSHOT_PLUGIN_OPTIONS');

@Injectable()
export class NgxsSnapshotPlugin implements NgxsPlugin {

    private defaultStates = {};

    constructor(
        private logger: LoggerService,
        @Inject(NGXS_SNAPSHOT_PLUGIN_OPTIONS) private readonly options: NgxsSnapshotPluginOptions,
    ) {
        const defaultOptions: NgxsSnapshotPluginOptions = {
            groups: []
        };
        this.options = Object.assign({}, defaultOptions, this.options);
    }

    public handle(state, action, next) {
        const matches = actionMatcher(action);
        const isInitAction = matches(InitState) || matches(UpdateState);
        const isRestoreAction = matches(RestoreSnapshot);
        const isClearAction = matches(ClearSnapshots);

        if (matches(InitState)) {
            this.options.groups.forEach((group) => {
                const groupDefaults = {};
                group.keys.forEach((keyObject) => {
                    groupDefaults[keyObject.key] = getValue(state, keyObject.key);
                });
                this.defaultStates[group.name] = groupDefaults;
            });
        }

        if (!isInitAction && !isRestoreAction && !isClearAction) {

            const currentSnapshotState = getValue(state, 'snapshots');

            // get all groups affected by this action
            const triggeredGroups = this.getTriggeredGroups(matches);

            loopGroups: for (const group of triggeredGroups) {
                let currentGroupState = getValue(currentSnapshotState, `groups.${group.name}`) || [];

                const groupState = {};
                // get the substates in this group
                for (const keyObject of group.keys) {
                    const value = getValue(state, keyObject.key);
                    if ( keyObject.required && (!value || isEmpty(value)) ) {
                        continue loopGroups;
                    }
                    groupState[keyObject.key] = getValue(state, keyObject.key);
                }
                const isDefault = currentGroupState.length === 0
                                    && isEqual(this.defaultStates[group.name], groupState);
                const isUnchanged = isEqual(currentGroupState[currentGroupState.length - 1], groupState);
                if (!isDefault && !isUnchanged) {
                    currentGroupState = [
                        ...currentGroupState,
                        groupState,
                    ];
                    state = setValue(state, `snapshots.groups.${group.name}`, currentGroupState);
                }
            }
        }

        if (isRestoreAction) {
            const restoreAction: RestoreSnapshot = (action as RestoreSnapshot);
            const group: NgxsSnapshotPluginGroupOptions
                = this.options.groups.find(g => g.name === restoreAction.payload.group);

            let snapshots = getValue(state, `snapshots.groups.${group.name}`);
            let snapshot;
            if (Array.isArray(snapshots) && snapshots.length > 0) {
                [snapshots, snapshot] = DashGisImmutable.pop(snapshots);
                state = setValue(state, `snapshots.groups.${group.name}`, snapshots);

                for (const keyObject of group.keys) {
                    state = setValue(state, keyObject.key, snapshot[keyObject.key]);
                }
            }
        }

        if (isClearAction) {
            const clearAction: ClearSnapshots = (action as ClearSnapshots);
            const group: NgxsSnapshotPluginGroupOptions
                = this.options.groups.find(g => g.name === clearAction.payload.group);

            state = setValue(state, `snapshots.groups.${group.name}`, []);
        }

        return next(state, action);
    }

    private getTriggeredGroups(matches): NgxsSnapshotPluginGroupOptions[] {
        const triggered: NgxsSnapshotPluginGroupOptions[] = [];
        for (const group of this.options.groups) {
            for (const trigger of group.triggers) {
                if (matches(trigger)) {
                    triggered.push(group);
                    break;
                }
            }
        }
        return triggered;
    }
}
